import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { SearchCombinedUnitMetadata } from '@atlassian/search-client/config';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import type { AllFilterStates } from '../../../common/constants/filters/types';
import { ProductKeys, type PrimaryProductKey } from '../../../common/constants/product-keys';
import type {
	ThirdPartyConfigsBootstrap,
	ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import {
	convertQueryParamsToStringArray,
	getDatasourceFromId,
	parseQueryParams,
} from '../../../common/utils/query-params';
import type { QueryParams } from '../../../common/utils/query-params/types';
import { getDefaultSiteIds } from '../../../common/utils/sites/default-sites';
import { createAllFilterData } from '../../../controllers/store/search/filters/all-filter-data';
import { FilterUtils } from '../../../controllers/store/search/filters/filter-utils';

type GetSearchConfigArgs = {
	cloudId: string;
	queryParams: QueryParams;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	primaryProduct?: PrimaryProductKey;
	unitMetadata?: readonly SearchCombinedUnitMetadata[];
};

export const getSearchConfig = ({
	cloudId,
	queryParams,
	thirdPartyConfigs,
	primaryProduct,
	unitMetadata,
}: GetSearchConfigArgs): {
	cql: string | undefined;
	searchQuery: string;
	originalQuery: string | undefined;
	filters: Record<string, AllFilterStates | undefined>;
	selectedProducts: string[];
	selectedDatasource: ThirdPartyConfigsWithAPIResponse | undefined;
	sites: string[];
} => {
	// Parse query params so they're typed more strongly
	const parsedQueryParams = parseQueryParams(queryParams);
	const { lastModified_lt: _, lastModified_gt: __, ...parsedQueryParamsInUse } = parsedQueryParams;
	const queryParamsWithValuesAsStringArray = convertQueryParamsToStringArray(
		parsedQueryParamsInUse,
		Object.keys(parsedQueryParams),
	);

	const includeCustomerServiceManagement = fg('csm_product_filter_in_search');
	const allFilterData = createAllFilterData({ includeCustomerServiceManagement });
	const enabledCustomerServiceManagementProduct = includeCustomerServiceManagement
		? ProductKeys.CustomerServiceManagement
		: undefined;
	const productSelectionQueryParams: QueryParams = {
		...queryParams,
		product:
			parsedQueryParams.product === ProductKeys.CustomerServiceManagement
				? enabledCustomerServiceManagementProduct
				: queryParams.product,
	};

	// Get utilities for processing query params into filters
	const { getSelectedProducts } = FilterUtils(allFilterData, {});

	// Get selected products from query params
	const selectedProducts = getSelectedProducts(productSelectionQueryParams);

	// Get minimal filters object containing selected values based on query params
	const filters: Record<string, AllFilterStates | undefined> = Object.fromEntries(
		Object.entries(allFilterData).map(([filterId, filter]) => {
			return [
				filterId,
				filter.queryParams.getState(queryParamsWithValuesAsStringArray, cloudId) as AllFilterStates,
			];
		}),
	);

	// Get selected datasource
	const selectedDatasource = getDatasourceFromId(
		parsedQueryParams.datasource,
		selectedProducts[0],
		thirdPartyConfigs,
	);

	const cohort = serveFpsMultiSiteSearchExperiment({
		primaryProduct,
		fireExperimentExposure: false,
	});

	return {
		cql: parsedQueryParams.cql,
		searchQuery: parsedQueryParams.text || '',
		originalQuery: parsedQueryParams.originalQuery,
		filters,
		selectedProducts,
		selectedDatasource,
		// Gated by the multi-site experiment so `sites` is only populated for eligible users.
		sites:
			cohort !== 'control'
				? (parsedQueryParams.sites ??
					(cohort === 'test-2' ? getDefaultSiteIds(unitMetadata, cloudId) : []))
				: [],
	};
};
